import { Controller } from '@hotwired/stimulus';
import consumer from '../../channels/consumer';
import JSZip from 'jszip';

const BATCH_SIZE = 5;

export default class extends Controller {
  static values = {
    url: String,
    projectId: String,
    loadingText: String,
    downloadingText: String,
    generatingText: String,
    doneText: String,
    errorText: String,
    partialFailureText: String,
  };

  connect() {
    if (!this.hasProjectIdValue) return;

    this.channel = consumer.subscriptions.create(
      { channel: 'ProjectExportChannel', project_id: this.projectIdValue },
      {
        received: (data) => {
          if (data.status === 'export_ready') {
            this.element.style.display = '';
            this.element.classList.remove('hidden');
          }
        },
      }
    );
  }

  disconnect() {
    if (this.channel) {
      this.channel.unsubscribe();
    }
  }

  export(event) {
    event.preventDefault();

    if (this.loading) return;
    this.loading = true;

    const button = this.element;
    const originalText = button.textContent;

    button.textContent = this.loadingTextValue;
    button.classList.add('disabled');

    fetch(this.urlValue, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => this._buildZip(data, button))
      .catch((error) => {
        console.error(error);
        alert(this.errorTextValue.replace('%{error}', error.message));
        button.textContent = originalText;
        button.classList.remove('disabled');
        this.loading = false;
      });
  }

  async _buildZip(data, button) {
    const zip = new JSZip();
    const investors = data.investors;
    let downloaded = 0;
    let failedCount = 0;
    const total = investors.reduce((sum, inv) => sum + inv.documents.length, 0)
      + (data.subscriptions_export ? 1 : 0);

    const allTasks = investors.flatMap((investor) => {
      const folder = zip.folder(investor.folder_name);
      return investor.documents.map((doc) => ({ folder, doc }));
    });

    if (data.subscriptions_export) {
      allTasks.push({ folder: zip, doc: data.subscriptions_export });
    }

    for (let i = 0; i < allTasks.length; i += BATCH_SIZE) {
      const batch = allTasks.slice(i, i + BATCH_SIZE);

      const results = await Promise.allSettled(
        batch.map(async ({ folder, doc }) => {
          const response = await fetch(doc.url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          folder.file(doc.filename, blob);
        })
      );

      results.forEach((result, index) => {
        downloaded++;
        if (result.status === 'rejected') {
          failedCount++;
          console.warn(`Failed to download ${batch[index].doc.filename}: ${result.reason.message}`);
        }
      });

      button.textContent = this.downloadingTextValue
        .replace('%{downloaded}', downloaded)
        .replace('%{total}', total);
    }

    button.textContent = this.generatingTextValue;

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${data.zip_filename}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    if (failedCount > 0) {
      alert(this.partialFailureTextValue.replace('%{count}', failedCount));
    }

    button.textContent = this.doneTextValue;
    button.classList.remove('disabled');
    this.loading = false;
  }
}
